import { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Icon } from '@iconify/react';
import {
  Box,
  Grid,
  Container,
  Typography,
  Stack,
  Card,
  IconButton,
  MenuItem,
  CardContent,
  Table,
  TableBody,
  TableCell,
  Select,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  TextField,
  Tooltip
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import Chart from 'react-apexcharts';
import useGlobalSelector from '../store/Slice/Global/GlobalSelector';
import Page from '../components/Page';
import PageTitle from '../components/PageHeading';
import useLoginSelector from '../store/Slice/Login/LoginSelector';
import useDashboardSelector from '../store/Slice/Dashboard/DashboardSelector';
import { getDashboardData, getHierarchyData } from '../store/Slice/Dashboard/DashboardSlice';
import { getBankDropdownData } from '../store/Slice/Global/GlobalSlice';

// ─────────────────────────────────────────────
// HELPER: Format currency in Indian Format
// ─────────────────────────────────────────────

function formatCurrency(val) {
  if (val === undefined || val === null) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(val);
}

function formatCompactNumber(val) {
  if (val === undefined || val === null || val === 0) return '0';

  if (val >= 10000000) {
    const crValue = val / 10000000;
    return crValue >= 10 ? `${Math.round(crValue)} Cr` : `${crValue.toFixed(1)} Cr`;
  }

  if (val >= 100000) {
    const lValue = val / 100000;
    return lValue >= 10 ? `${Math.round(lValue)} L` : `${lValue.toFixed(1)} L`;
  }

  if (val >= 1000) {
    const kValue = val / 1000;
    return kValue >= 10 ? `${Math.round(kValue)} k` : `${kValue.toFixed(1)} k`;
  }

  return `${val}`;
}

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

function KpiCard({ title, icon, value, subValue, color, bgClass }) {
  return (
    <Card className={`analyticsDashCard ${bgClass}`} elevation={0}>
      <CardContent
        sx={{
          p: '1rem !important',
          '&:last-child': { pb: '1rem !important' },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        <Box className="displayFlex alignItemCenter justifyContentBetween" mb={1}>
          <Box className="disIconOuter" sx={{ backgroundColor: `${color}20` }}>
            <Box sx={{ color, fontSize: '1.25rem', display: 'flex' }}>{icon}</Box>
          </Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#212B36', mb: 0.25 }}>
          {value}
        </Typography>
        {subValue && (
          <Typography variant="body2" sx={{ color: '#718096', fontSize: '0.8rem' }}>
            {subValue}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ color: '#a0aec0', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
}

function PeriodToggle({ period, onChange }) {
  return (
    <Box className="analyticsPeriodToggle">
      <button
        type="button"
        className={`tabDefaultSec ${period === 'MTD' ? 'tabSelected' : ''}`}
        onClick={() => onChange('MTD')}
      >
        MTD
      </button>
      <button
        type="button"
        className={`tabDefaultSec ${period === 'YTD' ? 'tabSelected' : ''}`}
        onClick={() => onChange('YTD')}
      >
        YTD
      </button>
    </Box>
  );
}

function BankBarChart({ data }) {
  const options = {
    chart: {
      type: 'treemap',
      toolbar: { show: false },
      fontFamily: 'inherit'
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
        borderRadius: 4,
        dataLabels: {
          position: 'top'
        }
      }
    },
    fill: {
      opacity: 1
    },
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      style: {
        colors: ['#212B36'],
        fontSize: '11px',
        fontWeight: 600
      },
      formatter: function (val) {
        return formatCompactNumber(val);
      },
      offsetX: 25
    },
    xaxis: {
      type: 'logarithmic',
      categories: data.map((d) => d.bankName),
      labels: {
        style: { colors: '#718096', fontSize: '11px' },
        formatter: (val) => formatCompactNumber(val)
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { colors: '#212B36', fontSize: '12px', fontWeight: 500 },
        maxWidth: 150
      }
    },
    colors: ['#0d4689'],
    grid: {
      borderColor: '#eff1f5',
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { right: 60, left: 10 }
    },
    tooltip: {
      theme: 'light',
      custom: function ({ dataPointIndex }) {
        const item = data[dataPointIndex];

        return `
    <div class="analyticsTooltip">
      <div class="analyticsTooltipHeader">
        ${item.bankName}
      </div>
      <div class="analyticsTooltipBody">
        <div>
          <span>Total Volume:</span>
          <strong>${formatCurrency(item.totalAmount)}</strong>
        </div>
        <div>
          <span>Total Files:</span>
          <strong>${item.count}</strong>
        </div>
      </div>
    </div>
  `;
      }
    }
  };

  const series = [{ name: 'Total Amount', data: data.map((d) => d.totalAmount) }];

  const chartHeight = Math.max(350, data.length * 35);
  return <Chart options={options} series={series} type="bar" height={chartHeight} />;
}

function ProductDonutChart({ data }) {
  const options = {
    chart: {
      type: 'donut',
      fontFamily: 'inherit'
    },
    labels: data.map((d) => d.productName),
    colors: ['#0d4689', '#4fbbc8', '#81748f', '#5b8658', '#c4a35a'],
    legend: {
      position: 'bottom',
      labels: { colors: '#718096' }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: '11px' }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: (w) => {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return total;
              }
            }
          }
        }
      }
    },
    tooltip: {
      y: { formatter: (val) => `${val} cases` }
    }
  };

  const series = data.map((d) => d.count);

  return <Chart options={options} series={series} type="donut" height={350} />;
}

function DetailedBreakdownTable({ data }) {
  return (
    <TableContainer component={Paper} elevation={0} className="analyticsTableContainer">
      <Table size="small">
        <TableHead>
          <TableRow className="analyticsTableHead" sx={{ bgcolor: '#0d4689' }}>
            <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Bank</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Product</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff' }}>
              Files
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff', p: '0!important' }}>
              Sanctioned
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff', p: '2!important' }}>
              Disbursed
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff' }}>
              Amount
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff' }}>
              Conv %
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} className="analyticsTableRow">
              <TableCell sx={{ fontWeight: 500 }}>{row.bankName}</TableCell>
              <TableCell>{row.productName}</TableCell>
              <TableCell align="right">{row.files}</TableCell>
              <TableCell align="right">{row.sanctioned}</TableCell>
              <TableCell align="right">{row.disbursed}</TableCell>
              <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
              <TableCell align="right">
                <Chip
                  size="small"
                  label={`${row.conversionPercentage}%`}
                  className={
                    parseFloat(row.conversionPercentage) >= 70
                      ? 'analyticsConvHigh'
                      : parseFloat(row.conversionPercentage) >= 50
                      ? 'analyticsConvMid'
                      : 'analyticsConvLow'
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ProductDataTable({ data }) {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  return (
    <TableContainer component={Paper} elevation={0} className="analyticsTableContainer">
      <Table size="small">
        <TableHead>
          <TableRow className="analyticsTableHead" sx={{ bgcolor: '#0d4689' }}>
            <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Product</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff' }}>
              Cases
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff' }}>
              Amount
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: '#fff' }}>
              Share %
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} className="analyticsTableRow">
              <TableCell sx={{ fontWeight: 500 }}>{row.productName}</TableCell>
              <TableCell align="right">{row.count}</TableCell>
              <TableCell align="right">{formatCurrency(row.totalAmount)}</TableCell>
              <TableCell align="right">
                <Box className="analyticsShareBar">
                  <Box
                    className="analyticsShareFill"
                    style={{ width: `${((row.count / total) * 100).toFixed(0)}%` }}
                  />
                  <Typography variant="caption" className="analyticsShareText">
                    {((row.count / total) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

const MONTHS = [
  { id: 1, label: 'January' },
  { id: 2, label: 'February' },
  { id: 3, label: 'March' },
  { id: 4, label: 'April' },
  { id: 5, label: 'May' },
  { id: 6, label: 'June' },
  { id: 7, label: 'July' },
  { id: 8, label: 'August' },
  { id: 9, label: 'September' },
  { id: 10, label: 'October' },
  { id: 11, label: 'November' },
  { id: 12, label: 'December' }
];

const DAYS = [
  { id: 1, label: '1' },
  { id: 2, label: '2' },
  { id: 3, label: '3' },
  { id: 4, label: '4' },
  { id: 5, label: '5' },
  { id: 6, label: '6' },
  { id: 7, label: '7' },
  { id: 8, label: '8' },
  { id: 9, label: '9' },
  { id: 10, label: '10' },
  { id: 11, label: '11' },
  { id: 12, label: '12' },
  { id: 13, label: '13' },
  { id: 14, label: '14' },
  { id: 15, label: '15' },
  { id: 16, label: '16' },
  { id: 17, label: '17' },
  { id: 18, label: '18' },
  { id: 19, label: '19' },
  { id: 20, label: '20' },
  { id: 21, label: '21' },
  { id: 22, label: '22' },
  { id: 23, label: '23' },
  { id: 24, label: '24' },
  { id: 25, label: '25' },
  { id: 26, label: '26' },
  { id: 27, label: '27' },
  { id: 28, label: '28' },
  { id: 29, label: '29' },
  { id: 30, label: '30' },
  { id: 31, label: '31' }
];

const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

export default function DashboardApp() {
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { DashboardData, HierarchyData } = useDashboardSelector();

  const [period, setPeriod] = useState('MTD');
  const [filters, setFilters] = useState({
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const [selectedHierarchy, setSelectedHierarchy] = useState({
    partner: null,
    employee: null,
    product: null,
    bank: null
  });
  const [isOpenFilter, setOpenFilter] = useState(false);

  const { CityDrop, LoanTypeDrop, StatusDrop, SourceDrop, BankDrop } = useGlobalSelector();

  const fetchDashboardData = useCallback(() => {
    if (!LoginData?.userId) return;

    // prevent API until filters ready
    if (period === 'MTD' && !filters.month) return;
    if (period === 'YTD' && !filters.year) return;

    const payload = {
      type: period,
      month: period === 'MTD' ? filters.month : null,
      year: filters.year,
      day: period === 'MTD' ? filters.day : null,
      companyId: LoginData?.companyId,
      loanTypeId: selectedHierarchy?.product?.value || null,
      bankId: selectedHierarchy?.bank?.value || null,
      userId:
        selectedHierarchy.employee?.userId || selectedHierarchy.partner?.userId || LoginData?.userId
    };

    dispatch(getDashboardData(payload));
  }, [dispatch, LoginData, period, filters, selectedHierarchy]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const kpi = DashboardData?.kpiSummary || {
    loginCount: 0,
    loginVolume: 0,
    sanctionedCount: 0,
    sanctionedAmount: 0,
    disbursedCount: 0,
    disbursedAmount: 0,
    totalVolume: 0
  };

  const bankwiseData = DashboardData?.bankwiseData || [];
  const productwiseData = DashboardData?.productwiseData || [];
  const detailedBreakdown = DashboardData?.detailedBreakdown || [];

  function getTopBanks(data, limit = 10) {
    if (!Array.isArray(data)) return [];

    const sorted = [...data].sort((a, b) => b.totalAmount - a.totalAmount);

    const top = sorted.slice(0, limit);
    const rest = sorted.slice(limit);

    if (rest.length === 0) return top;

    const others = rest.reduce(
      (acc, cur) => {
        acc.count += cur.count;
        acc.totalAmount += cur.totalAmount;
        acc.encashmentAmount += cur.encashmentAmount || 0;
        return acc;
      },
      { bankName: 'Others', count: 0, totalAmount: 0, encashmentAmount: 0 }
    );

    return [...top, others];
  }

  const getBankData = useCallback(() => {
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  const bankChartData = useMemo(() => getTopBanks(bankwiseData, 100), [bankwiseData]);

  const FetchDashboardHierarchyData = useCallback(() => {
    if (!LoginData?.userId) return;
    const payload = {
      type: period,
      month: period === 'MTD' ? filters.month : null,
      year: filters.year,
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };

    dispatch(getHierarchyData(payload));
  }, [dispatch, LoginData, period, filters]);

  useEffect(() => {
    FetchDashboardHierarchyData();
  }, [FetchDashboardHierarchyData]);

  useEffect(() => {
    getBankData();
  }, [getBankData]);

  const partners = HierarchyData?.summary?.data?.partners || [];
  const employees = HierarchyData?.summary?.data?.employees || [];

  useEffect(() => {
    if (period === 'MTD') {
      setSelectedHierarchy({
        partner: null,
        employee: null,
        product: null,
        bank: null
      });

      setFilters({
        day: new Date().getDate(),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      });
    }

    if (period === 'YTD') {
      setSelectedHierarchy({
        partner: null,
        employee: null,
        product: null,
        bank: null
      });

      setFilters((prev) => ({
        ...prev,
        day: null,
        month: null
      }));
    }
  }, [period]);

  return (
    <Page title="Analytics Dashboard | Kpros">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4} className="dashaboardMain">
          {/* ── Page Header ── */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Analytics Dashboard" />
          </Stack>

          {/* ── Period Toggle & Filters ── */}
          {isOpenFilter && (
            <Grid container spacing={2} alignItems="center" sx={{ mt: 1, mb: 3 }}>
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  size="small"
                  clearOnEscape
                  options={LoanTypeDrop}
                  getOptionLabel={(option) => option.label || ''}
                  value={selectedHierarchy.product}
                  onChange={(event, newValue) => {
                    setSelectedHierarchy((prev) => ({
                      ...prev,
                      product: newValue
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Select Loan Type" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  size="small"
                  options={BankDrop}
                  getOptionLabel={(option) => option.label || ''}
                  value={selectedHierarchy.bank}
                  onChange={(event, newValue) => {
                    setSelectedHierarchy((prev) => ({
                      ...prev,
                      bank: newValue
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Select bank" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  size="small"
                  options={partners}
                  getOptionLabel={(option) => option.fullName || ''}
                  value={selectedHierarchy.partner}
                  onChange={(event, newValue) => {
                    setSelectedHierarchy((prev) => ({
                      ...prev,
                      partner: newValue
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Select Partner" />}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  size="small"
                  options={employees}
                  getOptionLabel={(option) => option.fullName || ''}
                  value={selectedHierarchy.employee}
                  onChange={(event, newValue) => {
                    setSelectedHierarchy((prev) => ({
                      ...prev,
                      employee: newValue
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Select Employee" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.3}>
                <Autocomplete
                  size="small"
                  clearOnEscape
                  options={DAYS}
                  getOptionLabel={(option) => option.label}
                  value={DAYS.find((d) => d.id === filters.day) ?? null}
                  onChange={(event, newValue) => {
                    setFilters((prev) => ({
                      ...prev,
                      day: newValue?.id ?? null
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Day" />}
                  disabled={period === 'YTD'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.3}>
                <Autocomplete
                  size="small"
                  clearOnEscape
                  options={MONTHS}
                  getOptionLabel={(option) => option.label}
                  value={MONTHS.find((m) => m.id === filters.month)}
                  onChange={(event, newValue) => {
                    setFilters((prev) => ({
                      ...prev,
                      month: newValue ? newValue.id : null
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Month" />}
                  disabled={period === 'YTD'}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1.3}>
                <Autocomplete
                  size="small"
                  clearOnEscape
                  options={YEARS}
                  value={filters.year}
                  onChange={(event, newValue) => {
                    setFilters((prev) => ({
                      ...prev,
                      year: newValue || null
                    }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Year" />}
                />
              </Grid>
            </Grid>
          )}

          {/* ── Period Toggle & Filters ── */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}
          >
            <Grid item xs={12} md={2}>
              <PeriodToggle period={period} onChange={setPeriod} />
            </Grid>
            <Tooltip title="Filter list">
              <IconButton
                onClick={(e) => {
                  if (isOpenFilter) {
                    setOpenFilter(false);
                  } else {
                    setOpenFilter(true);
                  }
                }}
              >
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Grid>

          {/* ── KPI Cards ── */}
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                title="Login Cases"
                icon={<DescriptionIcon />}
                value={kpi.loginCount}
                subValue={`Volume: ${formatCurrency(kpi.loginVolume)}`}
                color="#0d4689"
                bgClass="analyticsBg1"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                title="Sanctioned Amount"
                icon={<CheckCircleOutlineIcon />}
                value={kpi.sanctionedCount}
                subValue={`Amount: ${formatCurrency(kpi.sanctionedAmount)}`}
                color="#4fbbc8"
                bgClass="analyticsBg2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                title="Disbursed Amount"
                icon={<MonetizationOnIcon />}
                value={kpi.disbursedCount}
                subValue={`Amount: ${formatCurrency(kpi.disbursedAmount)}`}
                color="#587486"
                bgClass="analyticsBg3"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                title="Lead Encashment Amount"
                icon={<MonetizationOnIcon />}
                value={kpi.disbursedCount}
                subValue={`Amount: ${formatCurrency(kpi.leadEncashmentAmount)}`}
                color="#5b8658"
                bgClass="analyticsBg4"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                title="Payout Encashment Amount"
                icon={<MonetizationOnIcon />}
                value={kpi.disbursedCount}
                subValue={`Amount: ${formatCurrency(kpi.payoutEncashmentAmount)}`}
                color="#587886"
                bgClass="analyticsBg2"
              />
            </Grid>
          </Grid>

          {/* ── Charts ── */}
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} md={7}>
              <Card className="analyticsChartCard" elevation={0}>
                <Box className="analyticsCardHeader">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212B36' }}>
                    Bank-wise Performance
                  </Typography>
                  <Chip label={period} size="small" className="analyticsPeriodChip" />
                </Box>
                <CardContent sx={{ pt: 0 }}>
                  <Box sx={{ maxHeight: 350, overflowY: 'auto', overflowX: 'hidden' }}>
                    <BankBarChart data={bankChartData} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card className="analyticsChartCard" elevation={0}>
                <Box className="analyticsCardHeader">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212B36' }}>
                    Product-wise Distribution
                  </Typography>
                  <Chip label={period} size="small" className="analyticsPeriodChip" />
                </Box>
                <CardContent sx={{ pt: 0 }}>
                  <ProductDonutChart data={productwiseData} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Data Tables ── */}
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} md={12}>
              <Card className="analyticsChartCard" elevation={0}>
                <Box className="analyticsCardHeader">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212B36' }}>
                    Bank-wise Detailed Breakdown
                  </Typography>
                </Box>
                <CardContent sx={{ p: '0 !important' }}>
                  <DetailedBreakdownTable data={detailedBreakdown} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={12}>
              <Card className="analyticsChartCard" elevation={0}>
                <Box className="analyticsCardHeader">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212B36' }}>
                    Product-wise Breakdown
                  </Typography>
                </Box>
                <CardContent sx={{ p: '0 !important' }}>
                  <ProductDataTable data={productwiseData} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Page>
  );
}
